"use client";

import { ChevronDown } from "lucide-react";

import { joinClock, splitClock, type ClockParts } from "@/lib/study-plan-schedule";
import { cn } from "@/lib/utils";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

function minuteOptions(current: number) {
  if (MINUTES.includes(current)) return MINUTES;
  return [...MINUTES, current].sort((a, b) => a - b);
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

const segmentClass =
  "appearance-none overflow-hidden bg-transparent p-0 text-center tabular-nums text-gray-900 outline-none [text-align-last:center] disabled:cursor-not-allowed";

const fieldClass =
  "inline-flex shrink-0 items-center rounded-md border border-gray-200 bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20";

export function AmPmTimeInput({
  value,
  onChange,
  disabled,
  compact = false,
  "aria-label": ariaLabel,
}: {
  value: string;
  onChange: (hhmm: string) => void;
  disabled?: boolean;
  /** Tighter pair of fields for a day row. */
  compact?: boolean;
  "aria-label"?: string;
}) {
  const parts = splitClock(value);

  function commit(next: Partial<ClockParts>) {
    onChange(joinClock({ ...splitClock(value), ...next }));
  }

  return (
    <div className={cn("inline-flex shrink-0 items-center", compact ? "gap-1" : "gap-2")} aria-label={ariaLabel}>
      <div className={cn(fieldClass, compact ? "h-9 px-1" : "h-14 px-3")}>
        <select
          value={String(parts.hour12)}
          disabled={disabled}
          aria-label={ariaLabel ? `${ariaLabel} hour` : "Hour"}
          onChange={(event) => commit({ hour12: Number(event.target.value) })}
          className={cn(segmentClass, compact ? "h-9 w-6 text-sm" : "h-14 w-8 text-base")}
        >
          {HOURS.map((hour) => (
            <option key={hour} value={hour}>
              {hour}
            </option>
          ))}
        </select>
        <span className={cn("w-2 shrink-0 select-none text-center leading-none text-gray-500", compact ? "text-sm" : "text-base")} aria-hidden="true">
          :
        </span>
        <select
          value={String(parts.minute)}
          disabled={disabled}
          aria-label={ariaLabel ? `${ariaLabel} minutes` : "Minutes"}
          onChange={(event) => commit({ minute: Number(event.target.value) })}
          className={cn(segmentClass, compact ? "h-9 w-7 text-sm" : "h-14 w-9 text-base")}
        >
          {minuteOptions(parts.minute).map((minute) => (
            <option key={minute} value={minute}>
              {pad(minute)}
            </option>
          ))}
        </select>
      </div>
      <div className={cn(fieldClass, compact ? "h-9 pl-1.5 pr-1" : "h-14 pl-3 pr-2")}>
        <select
          value={parts.period}
          disabled={disabled}
          aria-label={ariaLabel ? `${ariaLabel} AM or PM` : "AM or PM"}
          onChange={(event) => {
            const period = event.target.value;
            if (period === "AM" || period === "PM") commit({ period });
          }}
          className={cn(segmentClass, "font-medium", compact ? "h-9 w-8 text-sm" : "h-14 w-10 text-base")}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
        <ChevronDown className="pointer-events-none h-3.5 w-3.5 shrink-0 text-gray-400" />
      </div>
    </div>
  );
}

/** One study day on a single row: name, hours, start, and end. */
export function StudyDayTimeRow({
  label,
  enabled,
  hours,
  onHoursChange,
  start,
  onStartChange,
  end,
  onEndChange,
}: {
  label: string;
  enabled: boolean;
  hours: string;
  onHoursChange: (value: string) => void;
  start: string;
  onStartChange: (value: string) => void;
  end: string;
  onEndChange: (value: string) => void;
}) {
  return (
    <div
      className={cn(
        "flex flex-nowrap items-center gap-1.5 rounded-xl border border-gray-100 px-2 py-1.5",
        enabled ? "" : "opacity-50",
      )}
    >
      <span className="w-[5.25rem] shrink-0 whitespace-nowrap text-sm text-gray-800">
        {label}
      </span>
      <div className="flex shrink-0 items-center gap-1">
        <input
          type="number"
          min="1"
          max="24"
          inputMode="numeric"
          value={enabled ? hours : ""}
          onChange={(e) => onHoursChange(e.target.value)}
          className="h-9 w-9 rounded-md border border-gray-200 bg-white px-1 text-center text-sm text-gray-900 outline-none [appearance:textfield] focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          disabled={!enabled}
          placeholder={enabled ? "hrs" : "-"}
          aria-label={`${label} hours`}
        />
        <span className="text-xs text-gray-500">hrs</span>
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <AmPmTimeInput
          value={start}
          onChange={onStartChange}
          disabled={!enabled}
          compact
          aria-label={`${label} start time`}
        />
        <span className="shrink-0 text-xs text-gray-400">to</span>
        <AmPmTimeInput
          value={end}
          onChange={onEndChange}
          disabled={!enabled}
          compact
          aria-label={`${label} end time`}
        />
      </div>
    </div>
  );
}
